import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarRange, Download, Loader2, Save, Trash2 } from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"]];
const LEVELS = ["beginner", "easy", "medium", "hard", "expert"];
const HUB_LABELS = { abacus: "Abacus", mental: "Mental Maths", vedic: "Vedic Maths" };
const sel = "w-full h-9 rounded-md border border-input bg-white px-2 text-sm";
const errMsg = (e, f) => { const d = e.response?.data?.detail; return Array.isArray(d) ? d.map((x) => x.msg).join(", ") : (typeof d === "string" ? d : f); };
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const upcomingWeeks = () => {
  const m = new Date(); m.setHours(0, 0, 0, 0); m.setDate(m.getDate() - ((m.getDay() + 6) % 7));
  return [0, 1, 2, 3, 4].map((i) => {
    const mon = new Date(m); mon.setDate(m.getDate() + i * 7);
    const fri = new Date(mon); fri.setDate(mon.getDate() + 4); fri.setHours(23, 59, 0, 0);
    return { start: ymd(mon), due: fri, label: `${i === 0 ? "This week" : i === 1 ? "Next week" : "Week"} · ${mon.toLocaleDateString([], { day: "numeric", month: "short" })} – ${fri.toLocaleDateString([], { day: "numeric", month: "short" })}` };
  }).filter((w) => w.due > new Date());
};

const blankDays = () => Object.fromEntries(DAYS.map(([d]) => [d, { on: false, hub: "abacus", activity: "", difficulty: "beginner" }]));

const DayRow = ({ day, label, v, cat, onChange }) => {
  const games = cat?.[v.hub] || [];
  return (
    <div className={`grid grid-cols-[110px_1fr] sm:grid-cols-[120px_1fr_1.4fr_1fr] gap-2 items-center rounded-xl px-3 py-2 transition-colors ${v.on ? "bg-primary/5" : "bg-slate-50"}`} data-testid={`plan-day-${day}`}>
      <label className="flex items-center gap-2 text-sm font-semibold">
        <input type="checkbox" checked={v.on} onChange={(e) => onChange({ on: e.target.checked })} data-testid={`plan-day-${day}-toggle`} />{label}
      </label>
      <select className={sel} disabled={!v.on} value={v.hub} onChange={(e) => onChange({ hub: e.target.value, activity: "" })} data-testid={`plan-day-${day}-hub`}>
        {Object.entries(HUB_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <select className={`${sel} col-start-2 sm:col-start-auto`} disabled={!v.on} value={v.activity || games[0]?.id || ""} onChange={(e) => onChange({ activity: e.target.value })} data-testid={`plan-day-${day}-activity`}>
        {games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
      </select>
      <select className={`${sel} col-start-2 sm:col-start-auto`} disabled={!v.on} value={v.difficulty} onChange={(e) => onChange({ difficulty: e.target.value })} data-testid={`plan-day-${day}-level`}>
        {LEVELS.map((l) => <option key={l} value={l}>{l[0].toUpperCase() + l.slice(1)}+</option>)}
      </select>
    </div>
  );
};

const SavedPlans = ({ plans, onLoad, onDelete }) => !plans.length ? null : (
  <div className="flex flex-wrap items-center gap-2" data-testid="saved-plans">
    <span className="text-xs font-semibold text-slate-600">Saved plans:</span>
    {plans.map((p) => (
      <span key={p.plan_id} className="inline-flex items-center rounded-full border bg-white text-sm">
        <button className="pl-3 pr-1 py-1 hover:text-primary" onClick={() => onLoad(p)} data-testid={`saved-plan-load-${p.plan_id}`}>{p.name}</button>
        <button className="pr-2 pl-1 py-1 text-slate-400 hover:text-red-600" onClick={() => onDelete(p)} data-testid={`saved-plan-delete-${p.plan_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </span>
    ))}
  </div>
);

export const WeeklyPlanForm = ({ classId, headers, onCreated }) => {
  const auth = headers.Authorization;
  const weeks = useMemo(upcomingWeeks, []);
  const [cat, setCat] = useState(null);
  const [plans, setPlans] = useState([]);
  const [days, setDays] = useState(blankDays);
  const [week, setWeek] = useState(weeks[0]?.start || "");
  const [opts, setOpts] = useState({ min_accuracy: 60, rounds_required: 1, note: "", save_as: "" });
  const [busy, setBusy] = useState(false);
  const loadPlans = useCallback(() => axios.get(`${API}/practice-plans`, { headers: { Authorization: auth } }).then((r) => setPlans(r.data.plans)).catch(() => {}), [auth]);
  useEffect(() => {
    axios.get(`${API}/practice-tasks/catalog`, { headers: { Authorization: auth } }).then((r) => setCat(r.data.catalog)).catch(() => toast.error("Couldn't load the game list"));
    loadPlans();
  }, [auth, loadPlans]);
  const setDay = (d, patch) => setDays((s) => ({ ...s, [d]: { ...s[d], ...patch } }));
  const payloadDays = () => DAYS.filter(([d]) => days[d].on).map(([d]) => ({
    day: d, hub: days[d].hub, activity: days[d].activity || cat?.[days[d].hub]?.[0]?.id, difficulty: days[d].difficulty,
    min_accuracy: Math.min(100, Math.max(0, Number(opts.min_accuracy) || 0)), rounds_required: Math.min(10, Math.max(1, Number(opts.rounds_required) || 1)), note: opts.note,
  }));
  const loadPlan = (p) => {
    const next = blankDays();
    p.days.forEach((d) => { next[d.day] = { on: true, hub: d.hub, activity: d.activity, difficulty: d.difficulty }; });
    setDays(next);
    if (p.days[0]) setOpts((o) => ({ ...o, min_accuracy: p.days[0].min_accuracy, rounds_required: p.days[0].rounds_required, note: p.days[0].note || "" }));
    toast.success(`Loaded "${p.name}"`);
  };
  const delPlan = async (p) => {
    if (!window.confirm(`Delete saved plan "${p.name}"?`)) return;
    try { await axios.delete(`${API}/practice-plans/${p.plan_id}`, { headers }); loadPlans(); } catch (e) { toast.error(errMsg(e, "Couldn't delete")); }
  };
  const run = async (fn) => {
    if (!payloadDays().length) return toast.error("Tick at least one day");
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errMsg(e, "Something went wrong")); } finally { setBusy(false); }
  };
  const savePlan = () => run(async () => {
    if (!opts.save_as.trim()) throw { response: { data: { detail: "Give the plan a name to save it" } } };
    await axios.post(`${API}/practice-plans`, { name: opts.save_as.trim(), days: payloadDays() }, { headers });
    toast.success("Plan saved"); setOpts({ ...opts, save_as: "" }); loadPlans();
  });
  const assign = () => run(async () => {
    const w = weeks.find((x) => x.start === week);
    if (!w) throw { response: { data: { detail: "Pick a week" } } };
    const r = await axios.post(`${API}/classes/${classId}/practice-plan`, { week_start: w.start, due_at: w.due.toISOString(), save_as: opts.save_as, days: payloadDays() }, { headers });
    toast.success(`${r.data.tasks.length} games assigned for the week${r.data.saved_plan ? " and plan saved" : ""}`);
    setDays(blankDays()); setOpts({ ...opts, save_as: "", note: "" }); loadPlans(); onCreated();
  });
  return (
    <div className="rounded-2xl border bg-white p-4 space-y-3" data-testid="weekly-plan-form">
      <p className="font-semibold flex items-center gap-2"><CalendarRange className="w-4 h-4 text-primary" />Weekly practice plan</p>
      <SavedPlans plans={plans} onLoad={loadPlan} onDelete={delPlan} />
      <div className="space-y-1.5">{DAYS.map(([d, l]) => <DayRow key={d} day={d} label={l} v={days[d]} cat={cat} onChange={(p) => setDay(d, p)} />)}</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <label className="text-xs font-semibold text-slate-600">Week
          <select className={`${sel} mt-1 h-10`} value={week} onChange={(e) => setWeek(e.target.value)} data-testid="plan-week">
            {weeks.map((w) => <option key={w.start} value={w.start}>{w.label}</option>)}
          </select></label>
        <label className="text-xs font-semibold text-slate-600">Pass mark (accuracy %)
          <Input type="number" min={0} max={100} className="mt-1" value={opts.min_accuracy} onChange={(e) => setOpts({ ...opts, min_accuracy: e.target.value })} data-testid="plan-min-accuracy" /></label>
        <label className="text-xs font-semibold text-slate-600">Rounds needed per game
          <Input type="number" min={1} max={10} className="mt-1" value={opts.rounds_required} onChange={(e) => setOpts({ ...opts, rounds_required: e.target.value })} data-testid="plan-rounds" /></label>
        <label className="text-xs font-semibold text-slate-600">Note for students (optional)
          <Input className="mt-1" maxLength={300} value={opts.note} onChange={(e) => setOpts({ ...opts, note: e.target.value })} data-testid="plan-note" /></label>
      </div>
      <p className="text-xs text-muted-foreground">Everything is due by the end of Friday. Students can play the games in any order.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input className="max-w-xs" maxLength={80} placeholder="Save as… (optional, e.g. Standard week)" value={opts.save_as} onChange={(e) => setOpts({ ...opts, save_as: e.target.value })} data-testid="plan-save-name" />
        <Button variant="outline" onClick={savePlan} disabled={busy} data-testid="plan-save-btn"><Save className="w-4 h-4 mr-1" />Save plan only</Button>
        <Button onClick={assign} disabled={busy || !cat} data-testid="plan-assign-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CalendarRange className="w-4 h-4 mr-1" />}Assign week to class</Button>
      </div>
    </div>
  );
};

export const PracticeReportButton = ({ classId, headers }) => {
  const today = new Date();
  const ago = new Date(); ago.setDate(today.getDate() - 30);
  const [range, setRange] = useState({ start: ymd(ago), end: ymd(today) });
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/classes/${classId}/practice-report`, { headers, params: { ...range, tz_offset: new Date().getTimezoneOffset() }, responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || "practice_report.xlsx";
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Report downloaded");
    } catch (e) {
      let d = null;
      try { d = e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : null; } catch { d = null; }
      toast.error(typeof d === "string" ? d : "Couldn't create the report");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border bg-white p-3 flex flex-wrap items-end gap-2" data-testid="practice-report">
      <label className="text-xs font-semibold text-slate-600">Tasks set from
        <Input type="date" className="mt-1 h-9" value={range.start} max={range.end} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="report-start" /></label>
      <label className="text-xs font-semibold text-slate-600">to
        <Input type="date" className="mt-1 h-9" value={range.end} min={range.start} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="report-end" /></label>
      <Button variant="outline" onClick={download} disabled={busy || !range.start || !range.end} data-testid="report-download-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}Download spreadsheet
      </Button>
    </div>
  );
};
